import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useApi, useCaller } from '@/hooks/api.hook';
import { setCardDetailAction, setCardError, setCardLoading, setCardsAction, updateCardAction } from '@/redux/slicers/card.slice';
import { updateWalletDetailCardAction } from '@/redux/slicers/wallet-detail.slice';
import type { Card, CardSecureDetails, NumberPage } from '@/types/financial-workspace';

export const useCardService = () => {
  const api = useApi('');
  const caller = useCaller();
  const dispatch = useDispatch();

  const listCards = useCallback(async (params: Record<string, unknown> = {}) => {
    dispatch(setCardLoading(true));
    dispatch(setCardError(''));
    const response = await caller<ApiResult<NumberPage<Card>>>(
      () => api.get('/v1/cards', params),
      { loadingKey: 'list-cards', errorMessage: 'Unable to load cards.' },
    );

    if (!response?.isSuccess || !response.data) {
      dispatch(setCardLoading(false));
      return null;
    }

    dispatch(setCardsAction(response.data));
    dispatch(setCardLoading(false));
    return response.data;
  }, [api, caller, dispatch]);

  const getCard = useCallback(async (cardId: string) => {
    const response = await caller<ApiResult<Card>>(
      () => api.get(`/v1/cards/${cardId}`),
      { loadingKey: 'get-card', errorMessage: 'Unable to load card details.' },
    );

    const card = response?.isSuccess ? response.data ?? null : null;
    dispatch(setCardDetailAction(card));
    return card;
  }, [api, caller, dispatch]);

  const getCardSecureDetails = useCallback(async (cardId: string) => {
    const response = await caller<ApiResult<CardSecureDetails>>(
      () => api.get(`/v1/cards/${cardId}/secure-details`, { includePan: true, includeCvv: true }),
      { loadingKey: 'get-card-secure-details', errorMessage: 'Unable to load card details.' },
    );

    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  const getCardControls = useCallback(async (cardId: string) => {
    const response = await caller<ApiResult<CardControls>>(
      () => api.get(`/v1/cards/${cardId}/controls`),
      { loadingKey: 'get-card-controls', errorMessage: 'Unable to load card controls.' },
    );

    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  const listCardMerchantCategories = useCallback(async () => {
    const response = await caller<ApiResult<CardControlCatalogItem[]>>(
      () => api.get('/v1/cards/merchant-categories'),
      { loadingKey: 'list-card-merchant-categories', errorMessage: 'Unable to load merchant categories.' },
    );

    return response?.isSuccess ? response.data ?? [] : [];
  }, [api, caller]);

  const listCardMerchants = useCallback(async (page = 1, pageSize = 50) => {
    const response = await caller<ApiResult<NumberPage<CardMerchantCatalogItem>>>(
      () => api.get('/v1/cards/merchants', { page, pageSize }),
      { loadingKey: 'list-card-merchants', errorMessage: 'Unable to load merchants.' },
    );

    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  const listCardCountries = useCallback(async () => {
    const response = await caller<ApiResult<CardControlCatalogItem[]>>(
      () => api.get('/v1/cards/countries'),
      { loadingKey: 'list-card-countries', errorMessage: 'Unable to load countries.' },
    );

    return response?.isSuccess ? response.data ?? [] : [];
  }, [api, caller]);

  const getCardHistory = useCallback(async (cardId: string, page = 1) => {
    const response = await caller<ApiResult<NumberPage<CardHistoryEntry>>>(
      () => api.get(`/v1/cards/${cardId}/history`, { page, pageSize: 20 }),
      { loadingKey: 'get-card-history', errorMessage: 'Unable to load card history.' },
    );

    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  const updateCardControls = useCallback(async (cardId: string, request: UpdateCardControlsRequest) => {
    const response = await caller<ApiResult<CardControls>>(
      () => api.patch(`/v1/cards/${cardId}/controls`, request, { headers: { 'Idempotency-Key': crypto.randomUUID() } }),
      { loadingKey: 'update-card-controls', successMessage: 'Card controls updated.', errorMessage: 'Unable to update card controls.' },
    );

    return response?.isSuccess ? response.data ?? null : null;
  }, [api, caller]);

  const renameCard = useCallback(async (card: Card, displayName: string) => {
    const response = await caller<ApiResult<CardControls>>(
      () => api.patch(
        `/v1/cards/${card.id}/controls`,
        { displayName },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      ),
      {
        loadingKey: 'rename-card',
        successMessage: 'Card renamed successfully.',
        errorMessage: 'Unable to rename card.',
      },
    );

    if (!response?.isSuccess || !response.data) return null;

    const updatedCard: Card = {
      ...card,
      displayName: response.data.displayName ?? displayName,
      updatedAt: response.data.updatedAt ?? card.updatedAt,
    };
    dispatch(updateCardAction(updatedCard));
    dispatch(updateWalletDetailCardAction(updatedCard));
    return updatedCard;
  }, [api, caller, dispatch]);

  const setCardFrozen = useCallback(async (cardId: string, frozen: boolean) => {
    const response = await caller<ApiResult<Card>>(
      () => api.post(`/v1/cards/${cardId}/${frozen ? 'freeze' : 'unfreeze'}`, frozen ? { reason: 'Frozen by card manager' } : {}),
      {
        loadingKey: frozen ? 'freeze-card' : 'unfreeze-card',
        successMessage: frozen ? 'Card frozen successfully.' : 'Card unfrozen successfully.',
        errorMessage: frozen ? 'Unable to freeze card.' : 'Unable to unfreeze card.',
      },
    );

    const card = response?.isSuccess ? response.data ?? null : null;
    if (card) dispatch(updateCardAction(card));
    return card;
  }, [api, caller]);

  const closeCard = useCallback(async (cardId: string, reason?: string) => {
    const response = await caller<ApiResult<Card>>(
      () => api.post(
        `/v1/cards/${encodeURIComponent(cardId)}/close`,
        { reason: reason?.trim() || null },
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      ),
      {
        loadingKey: 'close-card',
        successMessage: 'Card closed successfully.',
        errorMessage: 'Unable to close card.',
        returnErrorResponse: true,
      },
    );

    const card = response?.isSuccess ? response.data ?? null : null;
    if (card) {
      dispatch(updateCardAction(card));
      dispatch(updateWalletDetailCardAction(card));
    }
    return card;
  }, [api, caller, dispatch]);

  const createCard = useCallback(async (request: CreateCardRequest) => {
    const response = await caller<ApiResult<Card>>(
      () => api.post('/v1/cards', request, { headers: { 'Idempotency-Key': crypto.randomUUID() } }),
      {
        loadingKey: 'create-card',
        successMessage: 'Card created successfully.',
        errorMessage: 'Unable to create card.',
      },
    );

    const card = response?.isSuccess ? response.data ?? null : null;
    if (card) dispatch(updateCardAction(card));
    return card;
  }, [api, caller, dispatch]);

  return {
    listCards,
    getCard,
    getCardSecureDetails,
    getCardControls,
    listCardMerchantCategories,
    listCardMerchants,
    listCardCountries,
    getCardHistory,
    updateCardControls,
    renameCard,
    setCardFrozen,
    closeCard,
    createCard,
  };
};
